/**
 * Query telemetry — the pure core.
 *
 * THIS MEASURES THE PROBLEM. IT DOES NOT FIX IT.
 *
 * Why it exists. On 2026-09-17 19:44:34 UTC the owner's Cost Explorer died on
 * production with SQLSTATE 57014 — three hooks in one tick on a plain default
 * page load. That was attributable only because those hooks call logger.error
 * on failure:
 *
 *   - Postgres records NOTHING for a statement timeout on either database.
 *     `log_min_messages = fatal`, so an ERROR-level event produces no log line
 *     at all, and `log_min_error_statement = error` is a decoy because `fatal`
 *     suppresses the message before it applies.
 *   - The hooks log nothing when they SUCCEED, so silence from the thirteen
 *     other pages that read the same view cannot be told apart from "never
 *     opened".
 *   - `logging_collector = off`, so what little exists dies with the pod.
 *
 * The trend lives in the successes: the statement behind the failure had been
 * running at a mean of 5077ms against an 8000ms ceiling — 63% of budget — for
 * weeks, and nobody was receiving that number.
 */

/**
 * Role statement_timeout ceilings, read from `pg_roles.rolconfig` on both
 * staging and prod on 2026-09-17. These are the real budgets a read is
 * measured against, not chosen values: exceeding one is SQLSTATE 57014.
 *   anon          = {statement_timeout=3s}
 *   authenticated = {statement_timeout=8s}
 */
export const AUTHENTICATED_STATEMENT_TIMEOUT_MS = 8000;
export const ANON_STATEMENT_TIMEOUT_MS = 3000;

export type ReadOutcome = 'completed' | 'timed_out' | 'failed';

export interface OutcomeClassification {
  outcome: ReadOutcome;
  sqlstate: string | null;
}

/** Postgres query_canceled — what a blown statement_timeout returns. */
const PG_QUERY_CANCELED = '57014';

function errorCode(err: unknown): string | null {
  if (typeof err === 'object' && err !== null) {
    const code = (err as Record<string, unknown>).code;
    if (typeof code === 'string' && code) return code;
  }
  return null;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const m = (err as Record<string, unknown>).message;
    if (typeof m === 'string') return m;
  }
  return typeof err === 'string' ? err : '';
}

/**
 * A read's outcome. The distinction that matters is `timed_out` vs `failed`:
 * a timeout is a budget signal and belongs in the trend, a relation-missing or
 * network error is not and must never be counted as one.
 */
export function classifyReadOutcome(err: unknown): OutcomeClassification {
  if (err === null || err === undefined) return { outcome: 'completed', sqlstate: null };
  const code = errorCode(err);
  const message = errorMessage(err);
  if (code === PG_QUERY_CANCELED || /statement timeout|query canceled/i.test(message)) {
    return { outcome: 'timed_out', sqlstate: code ?? PG_QUERY_CANCELED };
  }
  return { outcome: 'failed', sqlstate: code };
}

/**
 * The share of its ceiling a read consumed. This is the number the owner asked
 * for — "where things are failing, and when" — because a duration alone makes
 * the reader remember the ceiling while a fraction does not.
 *
 * Deliberately NOT clamped at 1. A read measured at 17167ms against an 8000ms
 * ceiling is at 2.15, and how far over it is happens to be the useful part.
 */
export function budgetFraction(elapsedMs: number, budgetMs: number): number {
  if (!(budgetMs > 0)) return 0;
  return elapsedMs / budgetMs;
}

export interface QueryFilterShape {
  /** The selected range. A shape, not a value — it selects which rows, and it
   *  is the axis the owner wanted estimates keyed on. */
  period: string | null;
  /** Names of the filters that were set. Never their contents. */
  active: string[];
}

/**
 * Which filters were active — NEVER what they contained.
 *
 * Case numbers, departments, officer ids and caller names are personal and
 * case data. They must not reach a log, a telemetry row, or a metrics
 * dashboard. `period` is the single exception and is kept verbatim because it
 * names a range rather than a person, and the scaling analysis is keyed on it.
 */
export function filterShape(filters: Record<string, unknown>): QueryFilterShape {
  const active: string[] = [];
  let period: string | null = null;
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (key === 'period') {
      period = typeof value === 'string' && value ? value : null;
      continue;
    }
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (typeof value === 'boolean' && value === false) continue;
    if (typeof value === 'number' && Number.isNaN(value)) continue;
    active.push(key);
  }
  return { period, active };
}

export interface QueryTelemetryRecord {
  shape_key: string;
  surface: string;
  outcome: ReadOutcome;
  elapsed_ms: number;
  budget_ms: number;
  budget_fraction: number;
  /** null on a failure: an empty result must never be a failed read's answer. */
  row_count: number | null;
  sqlstate: string | null;
  filter_shape: QueryFilterShape;
  occurred_at: string;
}

export interface BuildQueryTelemetryInput {
  shapeKey: string;
  surface: string;
  elapsedMs: number;
  error: unknown;
  rowCount: number | null;
  filters?: Record<string, unknown>;
  budgetMs?: number;
  occurredAt?: string;
}

/**
 * One telemetry row for one read — success or failure, both recorded.
 *
 * A failure keeps its elapsed time. An estimator or trend built only from
 * completions learns only the fast cases, drifts optimistic, and then fails
 * with every monitor still green — the live defect on the AI prewarm path,
 * which records a duration only on success.
 */
export function buildQueryTelemetryRecord(
  input: BuildQueryTelemetryInput,
): QueryTelemetryRecord {
  const { outcome, sqlstate } = classifyReadOutcome(input.error);
  const budgetMs = input.budgetMs ?? AUTHENTICATED_STATEMENT_TIMEOUT_MS;
  return {
    shape_key: input.shapeKey,
    surface: input.surface,
    outcome,
    elapsed_ms: input.elapsedMs,
    budget_ms: budgetMs,
    budget_fraction: budgetFraction(input.elapsedMs, budgetMs),
    row_count: outcome === 'completed' ? input.rowCount : null,
    sqlstate,
    filter_shape: filterShape(input.filters ?? {}),
    occurred_at: input.occurredAt ?? new Date().toISOString(),
  };
}
